import { useState, useEffect, useRef } from "react";
import Papa from "papaparse";
import { toast } from "sonner";
import { batchAPI } from "../../lib/api";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const fitColor = s => s >= 75 ? "#22C55E" : s >= 45 ? "#F59E0B" : "#EF4444";
const Badge = ({ label, color = ACCENT }) => (
  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px" }}>{label}</span>
);

const BATCH_RESULTS_MOCK = [];

/* ── Upload Zone ── */
const UploadZone = ({ onUpload }) => {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState(null);
  const fileRef = useRef(null);

  // Strip leading/trailing whitespace + handle BOM-prefixed headers from Excel
  const norm = (k) => (k || "").replace(/^\uFEFF/, "").trim().toLowerCase();
  // Map a row's keys (case/whitespace insensitive) onto the canonical schema.
  const rowToCanonical = (raw) => {
    const lookup = {};
    for (const k of Object.keys(raw)) lookup[norm(k)] = raw[k];
    const get = (key) => (lookup[key] != null ? String(lookup[key]).trim() : "");
    return {
      company_input: get("company name") || get("company") || "",
      domain: get("domain") || "",
      country: get("country") || "",
      target_persona: get("target persona") || get("persona") || "",
      notes: get("notes") || "",
    };
  };

  const isExampleRow = (companyName) =>
    /^EXAMPLE(\s|-|$)/i.test((companyName || "").trim());

  const parseFile = (f) => {
    setParseError(null);
    const lower = f.name.toLowerCase();
    if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) {
      setParseError("XLSX is not yet supported by the in-browser parser. Please save as CSV (UTF-8) and re-upload.");
      setParsedRows([]);
      setPreview([]);
      return;
    }
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = (result.data || [])
          .map(rowToCanonical)
          .filter((r) => r.company_input && !isExampleRow(r.company_input));
        if (rows.length === 0) {
          setParseError("No valid rows found. Check that the file has a header row including 'Company Name' and that at least one non-EXAMPLE row has a Company Name.");
          setParsedRows([]);
          setPreview([]);
          return;
        }
        setParsedRows(rows);
        // Lightweight preview — first 5 rows of the parsed file
        setPreview(rows.slice(0, 5).map((r) => ({
          company: r.company_input, domain: r.domain || "—",
          country: r.country || "—", persona: r.target_persona || "—",
          notes: r.notes || "—",
        })));
      },
      error: (err) => {
        setParseError(`CSV parse error: ${err?.message || "unknown"}`);
        setParsedRows([]);
        setPreview([]);
      },
    });
  };

  const handleDrop = e => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); parseFile(f); }
  };

  const handleSelect = e => {
    const f = e.target.files[0];
    if (f) { setFile(f); parseFile(f); }
  };

  const downloadTemplate = () => {
    const rows = [
      "Company Name,Domain,Country,Target Persona,Notes",
      "EXAMPLE - Example Corp,example.com,United States,CRO/VP Sales,Delete this row and add your companies below",
    ];
    // 50 empty rows so users can type directly into spreadsheet cells
    for (let i = 0; i < 50; i++) rows.push(",,,,");
    const csv = rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inference_batch_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragging ? ACCENT : file ? "#22C55E" : BORDER}`,
          borderRadius: 12, padding: "40px 20px", textAlign: "center", cursor: "pointer",
          background: dragging ? `${ACCENT}08` : file ? "#022E0E" : "transparent",
          transition: "all 0.2s", marginBottom: 20,
        }}
      >
        <input ref={fileRef} type="file" accept=".csv,.xlsx" style={{ display: "none" }} onChange={handleSelect} />
        {file ? (
          <>
            <div style={{ fontSize: 28, marginBottom: 10 }}>{parseError ? "⚠" : "✓"}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: parseError ? "#F59E0B" : "#22C55E", marginBottom: 4 }}>{file.name}</div>
            <div data-testid="batch-parse-summary" style={{ fontSize: 12, color: parseError ? "#F59E0B" : TEXT_DIM }}>
              {parseError ? parseError : `${parsedRows.length} valid ${parsedRows.length === 1 ? "row" : "rows"} detected`}
            </div>
          </>
        ) : (
          <>
            <svg width={36} height={36} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={1.5} style={{ margin: "0 auto 12px", display: "block" }}>
              <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>Drop CSV or XLSX here</div>
            <div style={{ fontSize: 12, color: TEXT_DIM }}>or click to browse · Required columns: Company Name, Domain, Country — optional: Target Persona, Notes. Max 500 companies per batch.</div>
          </>
        )}
      </div>

      {preview.length > 0 && (
        <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY }}>Preview</span>
            <Badge label={`${preview.length} rows`} color="#22C55E" />
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Company", "Domain", "Country", "Persona", "Notes"].map(h => (
                    <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", borderBottom: `1px solid ${BORDER}`, fontWeight: 600, letterSpacing: "0.06em" }}>{h.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i} style={{ borderBottom: i < preview.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                    {Object.values(r).map((v, j) => (
                      <td key={j} style={{ padding: "9px 14px", color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button
          data-testid="batch-run-btn"
          onClick={() => parsedRows.length && onUpload(parsedRows)}
          style={{
            background: parsedRows.length ? ACCENT : "#1E3A6E",
            color: parsedRows.length ? "#fff" : "#6B9AE0",
            border: "none", borderRadius: 9, padding: "11px 24px",
            fontSize: 14, fontWeight: 600, cursor: parsedRows.length ? "pointer" : "not-allowed",
            fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
          }}
        >Run Batch Analysis →</button>
      </div>

      <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center" }}>
        <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
        <span style={{ fontSize: 11, color: TEXT_DIM }}>
          Download{" "}
          <span data-testid="batch-download-template" onClick={(e) => { e.stopPropagation(); downloadTemplate(); }} style={{ color: ACCENT, cursor: "pointer", textDecoration: "underline" }}>template CSV</span>
          {" "}· Max 500 companies per batch · Each analysis consumes 1 run credit
        </span>
      </div>
      <div data-testid="batch-template-helper-note" style={{ marginTop: 6, marginLeft: 21, fontSize: 11, color: TEXT_MUTED }}>
        Template includes 1 example row and 50 empty rows. Delete the example row before uploading.
      </div>
    </div>
  );
};

/* ── Batch Results Table ── */
const BatchResultsTable = ({ results, onViewAnalysis }) => {
  const [selected, setSelected] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortField, setSortField] = useState("fitScore");
  const [sortDir, setSortDir] = useState("desc");

  const toggleSelect = id => {
    setSelected(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => setSelected(s => s.size === filtered.length ? new Set() : new Set(filtered.map((_, i) => i)));

  const handleSort = f => {
    if (sortField === f) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(f); setSortDir("desc"); }
  };

  const filtered = results
    .filter(r => filterStatus === "All" || r.status === filterStatus)
    .sort((a, b) => {
      const v = (x) => typeof x[sortField] === "number" ? x[sortField] : 0;
      return sortDir === "desc" ? v(b) - v(a) : v(a) - v(b);
    });

  const SortHeader = ({ label, field }) => (
    <th
      onClick={() => handleSort(field)}
      style={{ padding: "9px 14px", textAlign: "left", fontSize: 10, color: sortField === field ? ACCENT : TEXT_DIM, fontFamily: "'DM Mono', monospace", borderBottom: `1px solid ${BORDER}`, fontWeight: 600, letterSpacing: "0.06em", cursor: "pointer", whiteSpace: "nowrap", userSelect: "none" }}
    >
      {label} {sortField === field ? (sortDir === "desc" ? "↓" : "↑") : ""}
    </th>
  );

  return (
    <div>
      {results.length === 0 && (
        <div data-testid="batch-empty-state" style={{ textAlign: "center", padding: "60px 16px", border: `1px dashed ${BORDER}`, borderRadius: 8, marginBottom: 16 }}>
          <p style={{ fontSize: 15, color: TEXT_PRIMARY, marginBottom: 8 }}>No batch runs yet.</p>
          <p style={{ fontSize: 13, color: TEXT_MUTED }}>Upload a CSV to analyze multiple companies at once.</p>
        </div>
      )}
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {["All", "completed", "failed", "processing"].map(f => (
            <button key={f} onClick={() => setFilterStatus(f)} style={{
              fontSize: 11, padding: "5px 12px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${filterStatus === f ? ACCENT : BORDER}`,
              background: filterStatus === f ? `${ACCENT}18` : "transparent",
              color: filterStatus === f ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
              textTransform: "capitalize",
            }}>{f}</button>
          ))}
        </div>
        {selected.size > 0 && (
          <div style={{ display: "flex", gap: 8, marginLeft: "auto", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: TEXT_MUTED, fontFamily: "'DM Mono', monospace" }}>{selected.size} selected</span>
            {[
              { label: "Export CSV", color: "#22C55E" },
              { label: "Add to Monitor", color: ACCENT },
              { label: "Export JSON", color: TEXT_MUTED },
            ].map(b => (
              <button key={b.label} style={{
                background: `${b.color}14`, border: `1px solid ${b.color}33`,
                borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 600,
                color: b.color, cursor: "pointer", fontFamily: "'DM Mono', monospace",
              }}>{b.label}</button>
            ))}
          </div>
        )}
        {selected.size === 0 && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button style={{
              background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 6,
              padding: "6px 12px", fontSize: 11, color: ACCENT, cursor: "pointer", fontFamily: "'DM Mono', monospace",
            }}>EXPORT ALL CSV</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: SURFACE_2 }}>
                <th style={{ padding: "9px 14px", width: 36 }}>
                  <input
                    type="checkbox"
                    checked={selected.size === filtered.length && filtered.length > 0}
                    onChange={toggleAll}
                    style={{ cursor: "pointer", accentColor: ACCENT }}
                  />
                </th>
                <SortHeader label="COMPANY" field="name" />
                <SortHeader label="FIT" field="fitScore" />
                <th style={{ padding: "9px 14px", fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", borderBottom: `1px solid ${BORDER}`, fontWeight: 600, letterSpacing: "0.06em" }}>INDUSTRY</th>
                <th style={{ padding: "9px 14px", fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", borderBottom: `1px solid ${BORDER}`, fontWeight: 600, letterSpacing: "0.06em" }}>PAYMENTS</th>
                <th style={{ padding: "9px 14px", fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", borderBottom: `1px solid ${BORDER}`, fontWeight: 600, letterSpacing: "0.06em" }}>BNPL</th>
                <SortHeader label="IDENTITY" field="identConf" />
                <th style={{ padding: "9px 14px", fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", borderBottom: `1px solid ${BORDER}`, fontWeight: 600, letterSpacing: "0.06em" }}>TIER</th>
                <th style={{ padding: "9px 14px", fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", borderBottom: `1px solid ${BORDER}`, fontWeight: 600, letterSpacing: "0.06em" }}>STATUS</th>
                <th style={{ padding: "9px 14px", fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", borderBottom: `1px solid ${BORDER}`, fontWeight: 600, letterSpacing: "0.06em" }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr
                  key={i}
                  style={{
                    borderBottom: i < filtered.length - 1 ? `1px solid ${BORDER}` : "none",
                    background: selected.has(i) ? `${ACCENT}08` : "transparent",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => { if (!selected.has(i)) e.currentTarget.style.background = SURFACE_2; }}
                  onMouseLeave={e => { if (!selected.has(i)) e.currentTarget.style.background = "transparent"; }}
                >
                  <td style={{ padding: "10px 14px" }}>
                    <input type="checkbox" checked={selected.has(i)} onChange={() => toggleSelect(i)} style={{ cursor: "pointer", accentColor: ACCENT }} />
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: fitColor(r.fitScore), flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{r.name}</div>
                        <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{r.domain}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: fitColor(r.fitScore), fontFamily: "'DM Sans', sans-serif" }}>{r.fitScore}</span>
                  </td>
                  <td style={{ padding: "10px 14px", color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>{r.industry}</td>
                  <td style={{ padding: "10px 14px" }}>
                    {r.payments ? <span style={{ fontSize: 11, color: TEXT_MUTED }}>{r.payments}</span> : <span style={{ fontSize: 11, color: TEXT_DIM }}>—</span>}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: r.bnpl ? "#22C55E" : TEXT_DIM }} />
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontSize: 12, color: r.identConf >= 90 ? "#22C55E" : r.identConf >= 75 ? "#F59E0B" : "#EF4444", fontFamily: "'DM Mono', monospace" }}>
                      {r.status === "completed" ? `${r.identConf}%` : "—"}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    {r.status === "completed" && <Badge label={`TIER ${r.tier}`} color={r.tier === 1 ? "#22C55E" : "#F59E0B"} />}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <Badge
                      label={r.status.toUpperCase()}
                      color={r.status === "completed" ? "#22C55E" : r.status === "failed" ? "#EF4444" : "#F59E0B"}
                    />
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    {r.status === "completed" && r.analysis_id ? (
                      <button
                        data-testid={`batch-row-open-${r.analysis_id}`}
                        onClick={() => onViewAnalysis && onViewAnalysis(r.analysis_id)}
                        style={{
                        background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 5,
                        padding: "4px 10px", fontSize: 10, color: ACCENT, cursor: "pointer", fontFamily: "'DM Mono', monospace",
                      }}>OPEN</button>
                    ) : r.status === "failed" ? (
                      <button style={{
                        background: "#200A0A", border: "1px solid #7F1D1D", borderRadius: 5,
                        padding: "4px 10px", fontSize: 10, color: "#EF4444", cursor: "pointer", fontFamily: "'DM Mono', monospace",
                      }}>RETRY</button>
                    ) : (
                      <span style={{ fontSize: 10, color: "#F59E0B", fontFamily: "'DM Mono', monospace" }}>RUNNING…</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div style={{ marginTop: 14, display: "flex", gap: 20 }}>
        {[
          { label: "Completed", value: results.filter(r => r.status === "completed").length, color: "#22C55E" },
          { label: "Failed", value: results.filter(r => r.status === "failed").length, color: "#EF4444" },
          { label: "Processing", value: results.filter(r => r.status === "processing").length, color: "#F59E0B" },
          { label: "BNPL detected", value: results.filter(r => r.bnpl).length, color: ACCENT },
        ].map(s => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: s.color }} />
            <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{s.label}: <strong style={{ color: s.color }}>{s.value}</strong></span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── ROOT ── */
export default function InferenceBatch({ workspaceId, onViewAnalysis }) {
  const [view, setView] = useState("upload");
  const [loading, setLoading] = useState(false);
  const [batchResults, setBatchResults] = useState([]);
  const [batchMeta, setBatchMeta] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    const fetchBatches = async () => {
      try {
        const res = await batchAPI.list(workspaceId);
        const latest = (res.data && res.data.length > 0) ? res.data[0] : null;
        if (latest) {
          setBatchMeta(latest);
          const mapped = (latest.results || []).map(r => ({
            name: r.company_name || "Unknown",
            domain: r.domain || "N/A",
            industry: r.domain ? r.domain.replace(/\.\w+$/, "").replace(/^www\./, "") : "N/A",
            region: "N/A",
            fitScore: r.fit_score || 0,
            conf: r.fit_score >= 80 ? "High" : r.fit_score >= 60 ? "Medium" : "Low",
            identConf: 85,
            status: r.status || "completed",
            bnpl: false,
            payments: "N/A",
            tier: r.fit_score >= 80 ? 1 : r.fit_score >= 60 ? 2 : 3,
            outputs: 4,
            analysis_id: r.analysis_id || null,
          }));
          setBatchResults(mapped);
        } else {
          setBatchResults([]);
          setBatchMeta(null);
        }
      } catch (err) {
        console.error("Failed to fetch batches:", err);
        toast.error("Could not load batch results.");
      }
    };
    fetchBatches();
  }, [workspaceId]);

  const runBatch = async (companiesList) => {
    setLoading(true);
    if (workspaceId) {
      try {
        // Backwards-compatible: accept either an array of row objects (preferred)
        // or a flat array of strings (legacy fallback).
        const companies = (companiesList && companiesList.length > 0)
          ? companiesList
          : [{ company_input: "example.com" }, { company_input: "stripe.com" }];
        const res = await batchAPI.create(workspaceId, companies);
        if (res.data?.batch_id) {
          const batchId = res.data.batch_id;
          let attempts = 0;
          const poll = async () => {
            const status = await batchAPI.get(workspaceId, batchId);
            if (status.data.status === "completed" || attempts > 30) {
              setBatchMeta(status.data);
              if (status.data.results) {
                setBatchResults(status.data.results.map(r => ({
                  name: r.company_name || "Unknown",
                  domain: r.domain || "N/A",
                  industry: "N/A", region: "N/A",
                  fitScore: r.fit_score || 0,
                  conf: r.fit_score >= 80 ? "High" : r.fit_score >= 60 ? "Medium" : "Low",
                  identConf: 85, status: r.status || "completed",
                  bnpl: false, payments: "N/A",
                  tier: r.fit_score >= 80 ? 1 : 2, outputs: 4,
                })));
              }
              setLoading(false);
              setView("results");
              return;
            }
            attempts++;
            setTimeout(poll, 3000);
          };
          setTimeout(poll, 2000);
          return;
        }
      } catch (err) {
        console.error("Batch create failed:", err);
        toast.error("Batch upload failed. Please try again.");
      }
    }
    setTimeout(() => { setLoading(false); setView("results"); }, 2200);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #374151; }
      `}</style>

      {/* View toggle */}
      <div style={{ display: "flex", gap: 8, padding: "16px 28px 0" }}>
        {["upload", "results"].map(v => (
          <button key={v} data-testid={`batch-tab-${v}`} onClick={() => setView(v)} style={{
            fontSize: 12, padding: "6px 14px", borderRadius: 6, cursor: "pointer",
            border: `1px solid ${view === v ? ACCENT : BORDER}`,
            background: view === v ? `${ACCENT}18` : "transparent",
            color: view === v ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
            textTransform: "capitalize",
          }}>{v === "upload" ? "Upload" : "Results"}</button>
        ))}
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 28px" }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 16 }}>
            <div style={{ width: 44, height: 44, border: `3px solid ${ACCENT}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <p style={{ fontSize: 14, color: TEXT_MUTED }}>Queuing batch · 8 companies · Estimated 2–4 min…</p>
          </div>
        ) : view === "upload" ? (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em", marginBottom: 6 }}>Batch Upload</h2>
              <p style={{ fontSize: 13, color: TEXT_MUTED }}>Upload a CSV or XLSX list of companies to run analysis in parallel. Results available in the batch results view.</p>
            </div>
            <UploadZone onUpload={runBatch} />
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em", marginBottom: 4 }}>Batch Results</h2>
                <p style={{ fontSize: 13, color: TEXT_MUTED }}>Batch run completed · {batchResults.filter(r => r.status === "completed").length}/{batchResults.length} completed{batchMeta?.created_at ? ` · ${new Date(batchMeta.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}</p>
              </div>
              <button onClick={() => setView("upload")} style={{
                background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8,
                padding: "9px 16px", fontSize: 13, color: TEXT_MUTED, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
              }}>+ New Batch</button>
            </div>
            <BatchResultsTable results={batchResults} onViewAnalysis={onViewAnalysis} />
          </div>
        )}
      </div>
    </div>
  );
}
